import React, { useCallback, useEffect, useState } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import axios from 'axios';
import { useSelector } from 'react-redux';
import ComptableLayout from '../../layout/comptable-layout';
import MainLayout from '../../layout/main-layout';
import DuoPeriodPicker from '../../components/ui/duo-period-picker';
import { APP_API_URL, APP_NAME } from '../../utils';
import formatNumber, {
  defaultFilterDates,
  DUO_PERIOD_OPTIONS,
  formatDate,
  getPeriodDateRange,
} from '../../utils/functions';
import Loader from '../../components/loader';

import FinanceTaxReportPrint, { CHANNEL_ROWS } from '../../components/reports/FinanceTaxReportPrint';

const PERIOD_DEFAULT = { label: 'Ce mois', value: 'this_month' };
const periodOptions = [PERIOD_DEFAULT, ...DUO_PERIOD_OPTIONS.filter((p) => p.value !== 'this_month')];

const SUMMARY_CARDS = [
  { key: 'montant_ht', label: 'Total HT' },
  { key: 'tva', label: 'Total TVA' },
  { key: 'aib', label: 'Total AIB' },
  { key: 'montant_ttc', label: 'Total TTC', highlight: true },
];

const fmt = (value) => `${formatNumber(value ?? 0)} FCFA`;

const ReportTable = ({ report }) => {
  const cell = (bucket, field) => {
    if (field === 'nombre_ventes') return bucket?.[field] ?? 0;
    return fmt(bucket?.[field]);
  };

  return (
    <div className="duo-table-wrap">
      <table className="duo-table finance-tax-table">
        <thead>
          <tr>
            <th>Canal</th>
            <th className="text-end">Ventes</th>
            <th className="text-end">Montant HT</th>
            <th className="text-end">TVA</th>
            <th className="text-end">AIB</th>
            <th className="text-end">Montant TTC</th>
          </tr>
        </thead>
        <tbody>
          {CHANNEL_ROWS.map(({ key, label, isTotal }) => {
            const bucket = report[key];
            if (!bucket) return null;
            return (
              <tr key={key} className={isTotal ? 'finance-tax-table__total' : ''}>
                <td className={isTotal ? 'fw-bold' : ''}>{label}</td>
                <td className="text-end">{cell(bucket, 'nombre_ventes')}</td>
                <td className="text-end">{cell(bucket, 'montant_ht')}</td>
                <td className="text-end">{cell(bucket, 'tva')}</td>
                <td className="text-end">{cell(bucket, 'aib')}</td>
                <td className="text-end">{cell(bucket, 'montant_ttc')}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SummaryCards = ({ total }) => {
  if (!total) return null;

  return (
    <div className="finance-tax-summary-grid">
      {SUMMARY_CARDS.map(({ key, label, highlight }) => (
        <div
          key={key}
          className={`finance-tax-summary-card${highlight ? ' finance-tax-summary-card--highlight' : ''}`}
        >
          <p className="finance-tax-summary-card__label">{label}</p>
          <p className="finance-tax-summary-card__value">{fmt(total[key])}</p>
        </div>
      ))}
    </div>
  );
};

export default function FinanceImpotsPage() {
  const user = useSelector((state) => state.auth.user);
  const userRole = user?.personnels?.[0]?.poste_id;
  const isComptable = Number(userRole) === 8;
  const Layout = isComptable ? ComptableLayout : MainLayout;

  const [startDate, setStartDate] = useState(() => defaultFilterDates().dateFrom);
  const [endDate, setEndDate] = useState(() => defaultFilterDates().dateTo);
  const [selectedPeriod, setSelectedPeriod] = useState({ label: 'Personnalisé', value: '' });
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchReport = useCallback(async (customStart = null, customEnd = null) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = {};
      const useStart = customStart !== null ? customStart : startDate;
      const useEnd = customEnd !== null ? customEnd : endDate;
      if (useStart) params.start_date = useStart;
      if (useEnd) params.end_date = useEnd;

      const response = await axios.get(`${APP_API_URL}/finance-tax-report`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data?.success ? response.data : response.data;
      setReport(data?.periode ? data : null);
    } catch (error) {
      console.error('Erreur rapport finance :', error);
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const { dateFrom, dateTo } = defaultFilterDates();
    setStartDate(dateFrom);
    setEndDate(dateTo);
    fetchReport(dateFrom, dateTo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPeriod = (option) => {
    setSelectedPeriod(option);
    if (!option.value) return;
    const range = getPeriodDateRange(option.value);
    if (!range) return;
    const from = range.from.toISOString().slice(0, 10);
    const to = range.to.toISOString().slice(0, 10);
    setStartDate(from);
    setEndDate(to);
    fetchReport(from, to);
  };

  const handleApplyDates = (e) => {
    e.preventDefault();
    setSelectedPeriod(PERIOD_DEFAULT);
    fetchReport();
  };

  const exportToPDF = () => {
    if (!report) return;
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.text('Rapport Finance et Impôts', 14, 16);
    doc.setFontSize(10);
    doc.text(`Période : ${report.periode.debut} → ${report.periode.fin}`, 14, 24);

    const body = CHANNEL_ROWS.map(({ key, label }) => {
      const b = report[key] || {};
      return [
        label,
        String(b.nombre_ventes ?? 0),
        formatNumber(b.montant_ht),
        formatNumber(b.tva),
        formatNumber(b.aib),
        formatNumber(b.montant_ttc),
      ];
    });

    doc.autoTable({
      head: [['Canal', 'Ventes', 'HT', 'TVA', 'AIB', 'TTC']],
      body,
      startY: 32,
      headStyles: { fillColor: [88, 204, 2] },
      styles: { fontSize: 9 },
    });

    doc.save(`Finance Impots ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    if (!report) return;
    const rows = CHANNEL_ROWS.flatMap(({ key, label }) => {
      const b = report[key] || {};
      return [
        { Canal: label, Indicateur: 'Ventes', Valeur: b.nombre_ventes ?? 0 },
        { Canal: label, Indicateur: 'HT', Valeur: b.montant_ht },
        { Canal: label, Indicateur: 'TVA', Valeur: b.tva },
        { Canal: label, Indicateur: 'AIB', Valeur: b.aib },
        { Canal: label, Indicateur: 'TTC', Valeur: b.montant_ttc },
      ];
    });
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Finance');
    XLSX.writeFile(wb, `Finance Impots ${APP_NAME}.xlsx`);
  };

  const handlePrint = () => {
    window.print();
  };

  const periodLabel =
    report?.periode
      ? `${formatDate(`${report.periode.debut}T12:00:00`)} → ${formatDate(`${report.periode.fin}T12:00:00`)}`
      : '—';

  const total = report?.total;

  const pageContent = (
    <div className={`finance-tax-page${isComptable ? '' : ' duo-page'}`}>
      <div className="no-print">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Finance et Impôts</p>
          <h1 className="duo-hero-title">Synthèse fiscale showroom</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            {report?.periode
              ? `Période : ${periodLabel}`
              : 'Totaux HT, TVA, AIB et TTC — showroom et carreaux cassés.'}
          </p>
        </div>

        {total && (
          <div className="duo-kpi-row mb-3">
            <div className="duo-kpi-chip">
              <p className="duo-kpi-chip__label">Ventes (total)</p>
              <p className="duo-kpi-chip__value">{total.nombre_ventes}</p>
            </div>
            <div className="duo-kpi-chip">
              <p className="duo-kpi-chip__label">Montant TTC</p>
              <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
                {formatNumber(total.montant_ttc)} FCFA
              </p>
            </div>
            <div className="duo-kpi-chip">
              <p className="duo-kpi-chip__label">TVA + AIB</p>
              <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
                {formatNumber((total.tva ?? 0) + (total.aib ?? 0))} FCFA
              </p>
            </div>
            <div className="duo-kpi-chip">
              <p className="duo-kpi-chip__label">Montant HT</p>
              <p className="duo-kpi-chip__value" style={{ fontSize: '1rem' }}>
                {formatNumber(total.montant_ht)} FCFA
              </p>
            </div>
          </div>
        )}

        <section className="duo-panel mb-3">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Période &amp; actions
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button
                type="button"
                title="Rafraîchir"
                onClick={() => fetchReport()}
                className="duo-btn duo-btn--ghost duo-btn--icon"
              >
                <i className="bi bi-arrow-clockwise" />
              </button>
            </div>
          </div>

          <div className="px-3 pt-2 pb-2">
            <DuoPeriodPicker options={periodOptions} selectedOption={selectedPeriod} onSelect={applyPeriod} />
          </div>

          <div className="duo-filters border-top" style={{ borderColor: '#f0f0f0' }}>
            <form onSubmit={handleApplyDates}>
              <p className="small text-muted px-3 pt-2 mb-0">Plage personnalisée</p>
              <div className="duo-filters__grid px-3 pb-3">
                <div>
                  <label className="duo-form-label">Date de début</label>
                  <input
                    type="date"
                    className="duo-input"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
                <div>
                  <label className="duo-form-label">Date de fin</label>
                  <input
                    type="date"
                    className="duo-input"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>
              <div className="d-flex justify-content-end gap-2 px-3 pb-3">
                <button type="submit" className="duo-btn duo-btn--primary">
                  <i className="bi bi-search" /> Appliquer
                </button>
              </div>
            </form>
          </div>
        </section>

        <section className="duo-panel mb-3">
          <div className="finance-tax-panel__body">
            <h3 className="duo-section-title finance-tax-panel__title">Synthèse par canal</h3>
            <p className="small text-muted mb-3">
              Showroom et carreaux cassés — synthèse agrégée sans détail des achats.
            </p>

            {report ? (
              <>
                <ReportTable report={report} />
                <div className="mt-4">
                  <h3 className="duo-section-title">Indicateurs consolidés</h3>
                  <SummaryCards total={total} />
                </div>
              </>
            ) : (
              <p className="duo-empty-row mb-0">Aucune donnée pour cette période.</p>
            )}
          </div>
        </section>
      </div>

      <div className="finance-tax-print-only printable">
        <FinanceTaxReportPrint report={report} periodLabel={periodLabel} />
      </div>
    </div>
  );

  if (isLoading && !report) {
    return (
      <Layout>
        <Loader />
      </Layout>
    );
  }

  return (
    <Layout loading={isLoading}>
      {pageContent}
    </Layout>
  );
}
